import { useQuery } from "@tanstack/react-query";
import { ResponsiveContainer, BarChart, Bar, Tooltip, XAxis, YAxis } from "recharts";

export interface ViewTrendPoint {
  date: string;
  count: number;
}

interface ListingViewTrendProps {
  listingId: number;
  endpoint: "partner" | "admin";
  days?: number;
  height?: number;
  testId?: string;
}

export function ListingViewTrend({
  listingId,
  endpoint,
  days = 30,
  height = 80,
  testId,
}: ListingViewTrendProps) {
  const url =
    endpoint === "partner"
      ? `/api/partner/listings/${listingId}/view-trend?days=${days}`
      : `/api/admin/partner-listings/${listingId}/view-trend?days=${days}`;

  const { data, isLoading, isError } = useQuery<ViewTrendPoint[]>({
    queryKey: [
      endpoint === "partner" ? "/api/partner/listings" : "/api/admin/partner-listings",
      listingId,
      "view-trend",
      days,
    ],
    queryFn: async () => {
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) {
        throw new Error(`Failed to load view trend (${res.status})`);
      }
      const body = await res.json();
      if (!Array.isArray(body)) return [];
      return body.filter(
        (p): p is ViewTrendPoint =>
          p && typeof p === "object" && typeof p.date === "string" && typeof p.count === "number",
      );
    },
  });

  if (isError) {
    return (
      <div
        className="text-xs text-slate-400 flex items-center"
        style={{ height }}
        data-testid={testId ? `${testId}-error` : undefined}
      >
        Trend unavailable
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        className="text-xs text-slate-400"
        style={{ height }}
        data-testid={testId ? `${testId}-loading` : undefined}
      >
        Loading trend…
      </div>
    );
  }

  const points = data ?? [];
  const total = points.reduce((sum, p) => sum + (p.count || 0), 0);

  if (total === 0) {
    return (
      <div
        className="text-xs text-slate-400 flex items-center"
        style={{ height }}
        data-testid={testId ? `${testId}-empty` : undefined}
      >
        No views in the last {days} days
      </div>
    );
  }

  return (
    <div style={{ height, width: "100%" }} data-testid={testId}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={points} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
          <XAxis dataKey="date" hide />
          <YAxis hide allowDecimals={false} />
          <Tooltip
            cursor={{ fill: "rgba(59, 130, 246, 0.08)" }}
            contentStyle={{ fontSize: 12, padding: "4px 8px" }}
            labelFormatter={(label) => {
              const d = new Date(String(label));
              return isNaN(d.getTime())
                ? String(label)
                : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
            }}
            formatter={(val: number) => [`${val} view${val === 1 ? "" : "s"}`, "Unique"]}
          />
          <Bar dataKey="count" fill="#3b82f6" radius={[2, 2, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
